import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { IVariableToken } from "../variable-token";
import type { IVariableMentionAttributes } from "./variable-mention.types";

/**
 * Flattens a variable into the ProseMirror attributes of a `variableMention` node. ProseMirror attributes
 * always carry a value, so an absent subtype becomes `null`. The arrays are copied so the node never shares them.
 * @param variable The variable to store on the node.
 * @returns The node's attributes.
 */
export const variableTokenToAttributes = (variable: IVariableToken): IVariableMentionAttributes => ({
    type: variable.type,
    subtype: variable.subtype ?? null,
    values: Object.fromEntries(Object.entries(variable.values).map(([key, list]) => [key, [...list]])),
    title: variable.title,
    labels: [...variable.labels],
});

/**
 * The inverse of `variableTokenToAttributes`. A `null` subtype is dropped, because `serialiseVariableToken` would
 * treat it as present and reject it.
 * @param attributes The `variableMention` node's attributes.
 * @returns The variable the node stands for.
 */
export const variableTokenFromAttributes = ({
    type,
    subtype,
    values,
    title,
    labels,
}: IVariableMentionAttributes): IVariableToken =>
    subtype === null ? { type, values, title, labels } : { type, subtype, values, title, labels };

/**
 * Reads the variable a `variableMention` node stands for. ProseMirror types every attribute as `any`, so the
 * fields are picked off one by one into the typed shape rather than the whole record being cast.
 * @param node The `variableMention` node.
 * @returns The variable the node stands for.
 */
export const variableTokenFromNode = (node: ProseMirrorNode): IVariableToken => {
    const { type, subtype, values, title, labels } = node.attrs;

    return variableTokenFromAttributes({ type, subtype, values, title, labels });
};
